// MACHINERY V2 (Stroje) — the add-flow's own small parts (M2.7).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.7 + data package §10.
// Reference impl: design_handoff_machinery_v3/machinery-catalog.jsx —
// `PathCard` (lines 147-165), the ALEBO rule (188-190), `CRobotGlyph` (:32) and
// `CArrLGlyph` (:33). Artboards 0800 / 0801 / 0802.
//
// WHY THESE THREE GLYPHS ARE HERE AND NOT IN forms/FormGlyphs.tsx: that file's
// contract is "13x13, 1.4 stroke, decoration beside a field label". The robot
// is an 18px DIALOG-HEADER mark, the chevron is a back-LINK affordance and the
// magnifier belongs to a search control — none of them label a form field. The
// nine glyphs that DO label fields were added to the kit instead.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

/** 18px header mark for "Pridať zariadenie" (machinery-catalog.jsx:32). */
export const RobotGlyph: React.FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M3 15.5 L15 15.5" />
    <path d="M5.5 15.5 L5.5 12.5 L3.5 8 L8 4.5 L11.5 6" />
    <circle cx="12.5" cy="6.5" r="1.8" />
    <path d="M12.5 8.3 L12.5 11 L10 15.5" />
  </svg>
);

export const BackChevronGlyph: React.FC = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 13 13"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M8.5 2.5 L4.5 6.5 L8.5 10.5" />
  </svg>
);

/** Restated rather than imported: fleet/FleetToolbar.tsx's copy is a module
 *  local, not an export, and exporting it would make a toolbar file the home of
 *  an atom two surfaces draw. Identical markup (machinery-shared.jsx:572-577). */
export const SearchGlyph: React.FC = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden>
    <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" />
    <line x1="8.5" y1="8.5" x2="11.5" y2="11.5" stroke="currentColor" strokeLinecap="round" />
  </svg>
);

/** The "‹ Späť na možnosti" line under the CustomMachineDialog title
 *  (artboards 0802-0804). Feeds TabDialog's `subtitle` slot. */
export const BackLink: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      marginTop: 1,
      padding: 0,
      border: 0,
      background: "transparent",
      font: "inherit",
      fontSize: 12,
      color: "var(--mod-accent)",
      cursor: "pointer",
    }}
  >
    <BackChevronGlyph />
    {label}
  </button>
);

/** The hairline + centred mono "ALEBO" between the search bar and the cards. */
export const OrDivider: React.FC<{ label: string }> = ({ label }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0" }}>
    <span style={{ flex: 1, height: 1, background: "var(--border-soft)" }} />
    <span
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 9.5,
        letterSpacing: "0.1em",
        color: "var(--faint)",
        textTransform: "uppercase",
      }}
    >
      {label}
    </span>
    <span style={{ flex: 1, height: 1, background: "var(--border-soft)" }} />
  </div>
);

export interface PathCardProps {
  glyph: React.ReactNode;
  title: string;
  hint: string;
  /** Teal-tinted card — artboard 0801 gives it to "Prehľadávať katalóg". */
  primary?: boolean;
  onClick: () => void;
}

/** A `<button>`, not the designer's `<div>`: it is the whole hit target, so it
 *  must be focusable and Enter/Space-activatable. Nothing interactive is nested
 *  inside it, so the standing stopPropagation rule has nothing to guard here. */
export const PathCard: React.FC<PathCardProps> = ({ glyph, title, hint, primary, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      flex: 1,
      minWidth: 0,
      padding: "18px 16px 16px",
      borderRadius: 10,
      cursor: "pointer",
      font: "inherit",
      border: `1px solid ${primary ? "var(--mod-accent-line)" : "var(--border)"}`,
      background: primary ? "var(--mod-accent-soft)" : "var(--bg-soft)",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
      textAlign: "center",
    }}
  >
    <span
      style={{
        width: 42,
        height: 42,
        borderRadius: 12,
        background: "var(--card)",
        color: "var(--mod-accent)",
        border: "1px solid var(--mod-accent-line)",
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
      }}
    >
      {glyph}
    </span>
    <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>{title}</span>
    <span style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.5, maxWidth: 210 }}>
      {hint}
    </span>
  </button>
);
